import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { LabResultObservationRecord } from '@models/lab-result';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListBloodTypeObservationQuery,
  useListLaboratoryObservationsQuery,
  usePostLaboratoryObservationRecordMutation,
  useUpdateLaboratoryObservationRecordMutation,
  type PostLaboratoryObservationRecordMutation,
  type PostLaboratoryObservationRecordMutationVariables,
  type UpdateLaboratoryObservationRecordMutation,
  type UpdateLaboratoryObservationRecordMutationVariables,
} from 'generated/lab-result.generated';

export const useGetLabResults = (params: { code: string }) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListLaboratoryObservationsQuery({
    variables: {
      input: {
        patient: patient_id,
        code: params.code,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listLaboratoryObservations.data ?? [])
    .map((r) => new LabResultObservationRecord(r))
    .filter(({ code }) => (params.code ? code === params.code : true));
  return { ...response, data: records };
};

export const useGetBloodType = (skip?: boolean) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListBloodTypeObservationQuery({
    variables: {
      input: {
        patient: patient_id,
        code: 'blood-type',
      },
    },
    skip: !patient_id || skip,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listBloodTypeObservations.data ?? []).map(
    (r) => new LabResultObservationRecord(r)
  );
  return { ...response, data: records.at(0) };
};

export function useCreateLabObservation(): [
  (
    params: PostLaboratoryObservationRecordMutationVariables
  ) => Promise<FetchResult<PostLaboratoryObservationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostLaboratoryObservationRecordMutation();

  const loading = baseLoading;

  async function createLabObservation(
    params: PostLaboratoryObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postLaboratoryObservationRecord) {
          return;
        }
        const storeObject =
          response.postLaboratoryObservationRecord as unknown as StoreObject;
        try {
          cache.modify<{ listLaboratoryObservations: CachedRecordList }>({
            fields: {
              listLaboratoryObservations: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }

  return [createLabObservation, loading];
}

export function useUpdateLabObservation(): [
  (
    params: UpdateLaboratoryObservationRecordMutationVariables
  ) => Promise<FetchResult<UpdateLaboratoryObservationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateLaboratoryObservationRecordMutation();
  const loading = baseLoading;

  async function updateLabObservation(
    params: UpdateLaboratoryObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [updateLabObservation, loading];
}
